"use client";

import { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import styles from "./vimeoPlayer.module.scss";

export const ResponsiveVimeoEmbed = ({ 
  videoId, 
  autoplay = false, 
  muted = false, 
  loop = false,
  fillHeight = false,
  controls = false,
  color = '00adef',
  title = false,
  byline = false,
  portrait = false,
  className = '',
  style = {},
  onLoad = null,
  onError = null
}) => {
  const [aspectRatio, setAspectRatio] = useState(56.25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  // Fetch video data from Vimeo API to get dimensions
  useEffect(() => {

    if (!videoId) {
      setError('Video ID is required');
      setLoading(false);
      return;
    }

    const fetchVimeoData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use Vimeo's oEmbed API to get video dimensions
        const response = await fetch(
          `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}&width=1000`
        );

        if (!response.ok) {
          return;
          // throw new Error(`Failed to fetch video data: ${response.status}`);
        }

        const data = await response.json();

        if (data.width && data.height) {
          const calculatedRatio = (data.height / data.width) * 100;
          setAspectRatio(calculatedRatio);

          // if (onLoad) {
          //   onLoad({
          //     width: data.width,
          //     height: data.height,
          //     aspectRatio: calculatedRatio,
          //     title: data.title,
          //     author: data.author_name
          //   });
          // }
        }
      } catch (err) {
        console.error('Error fetching Vimeo video data:', err);
        setError(err.message);

        if (onError) {
          onError(err);
        }

        // Keep default aspect ratio on error
      } finally {
        setLoading(false);
      }
    };

    fetchVimeoData();
  }, [videoId, onLoad, onError]);

  // Build Vimeo embed URL with parameters
  const buildEmbedUrl = () => {
    if (!videoId) return '';

     const params = new URLSearchParams({
      autoplay: autoplay ? '1' : '0',
      muted: autoplay ? '1' : '0', // Always muted for background play
      loop: loop ? '1' : '0',
      color: color.replace('#', ''),
      title: title ? '1' : '0',
      byline: byline ? '1' : '0',
      portrait: portrait ? '1' : '0',
      responsive: '1',
      background: controls ? '0' : '1',
      controls: controls ? '1' : '0',
      pip: '0',
      quality: 'auto' 
    });

    return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
  };

  if (!videoId) {
    return (
      <div className={`vimeo-embed-error ${className}`} style={style}>
        <div className={clsx(styles.missing_id)}>
          Error: Video ID is required
        </div>
      </div>
    );
  }

  const wrapperStyle = {
    paddingBottom: fillHeight ? 0 : `${aspectRatio}%`,
    height: fillHeight ? '100%' : undefined,
    backgroundColor: loading ? '#f0f0f0' : '#000',
  };

  return (
    <div className={clsx(styles.crop_wrapper, fillHeight && styles.fillHeight)}>
      <div className={clsx(className, styles.wrapper, fillHeight && styles.wrapperFillHeight)} style={wrapperStyle}>
        
        {loading && (
          <div className={clsx(styles.loading)}>
            Loading video...
          </div>
        )}

        {error && (
          <div className={clsx(styles.loading, styles.error)}>
            {error}
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={buildEmbedUrl()}
          className={clsx(styles.iframe)}
          allowFullScreen
          allow="autoplay; fullscreen; picture-in-picture"
        />
      </div>
    </div>
  );

};